import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./ImportsPage.css";

const IMPORT_STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  open: "Abierta",
  in_transit: "En tránsito",
  arrived: "Arribada",
  customs: "En aduanas",
  cleared: "Nacionalizada",
  closed: "Cerrada",
  cancelled: "Cancelada",
};

type ImportRow = {
  id: string;
  import_number: string;
  status: string;
  purchase_order_id: string;
  purchase_number: string | null;
  origin_country_code: string | null;
  destination_country_code: string | null;
  incoterm: string | null;
  supplier_currency_code: string | null;
  shipment_date: string | null;
  estimated_arrival_date: string | null;
  arrival_date: string | null;
  customs_clearance_date: string | null;
  customs_declaration_number: string | null;
  cost_total_pen: number | string;
  declaration_count: number | string;
  updated_at: string;
};

type SupplierOption = {
  id: string;
  name: string;
  status: string;
};

type CatalogOption = {
  id: string;
  sku: string | null;
  name: string;
  cost_price: number | string;
  tax_rate: number | string;
  status: string;
};

type ManualImportLine = {
  catalogItemId: string;
  description: string;
  quantity: string;
  unitCost: string;
  taxRate: string;
};

type ManualImportDocument = {
  documentType: string;
  documentScope: string;
  series: string;
  documentNumber: string;
  issueDate: string;
  currencyCode: string;
  exchangeRate: string;
  isNational: boolean;
  taxRate: string;
  totalAmount: string;
  fobAmount: string;
  documentReference: string;
  notes: string;
};

type ManualImportCost = {
  costType: string;
  description: string;
  currencyCode: string;
  amount: string;
  exchangeRate: string;
  capitalizable: boolean;
  recoverableTax: boolean;
  allocationMethod: string;
};

type EligiblePurchaseOrder = {
  id: string;
  purchase_number: string;
  status: string;
  order_date: string;
  expected_date: string | null;
  currency_code: string;
  supplier_name: string;
  incoterm: string | null;
  remaining_item_count: number | string;
};

function money(value: number | string) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(Number(value || 0));
}

export function ImportsPage() {
  const { selectedOrganization } = useOrganization();
  const navigate = useNavigate();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [rows, setRows] = useState<ImportRow[]>([]);
  const [eligibleOrders, setEligibleOrders] = useState<EligiblePurchaseOrder[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [catalog, setCatalog] = useState<CatalogOption[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [purchaseOrderId, setPurchaseOrderId] = useState("");
  const [originCountryCode, setOriginCountryCode] = useState("");
  const [incoterm, setIncoterm] = useState("");
  const [shipmentDate, setShipmentDate] = useState("");
  const [estimatedArrivalDate, setEstimatedArrivalDate] = useState("");
  const [transportReference, setTransportReference] = useState("");
  const [allocationMethod, setAllocationMethod] = useState("value");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createMessage, setCreateMessage] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCreating, setManualCreating] = useState(false);
  const [manualSupplierId, setManualSupplierId] = useState("");
  const [manualCurrencyCode, setManualCurrencyCode] = useState("USD");
  const [manualOriginCountryCode, setManualOriginCountryCode] = useState("");
  const [manualIncoterm, setManualIncoterm] = useState("");
  const [manualShipmentDate, setManualShipmentDate] = useState("");
  const [manualEstimatedArrivalDate, setManualEstimatedArrivalDate] = useState("");
  const [manualTransportReference, setManualTransportReference] = useState("");
  const [manualAllocationMethod, setManualAllocationMethod] = useState("value");
  const [manualNotes, setManualNotes] = useState("");
  const [manualLines, setManualLines] = useState<ManualImportLine[]>([
    {
      catalogItemId: "",
      description: "",
      quantity: "1",
      unitCost: "0",
      taxRate: "0",
    },
  ]);
  const [manualDocuments, setManualDocuments] = useState<ManualImportDocument[]>([
    {
      documentType: "invoice",
      documentScope: "foreign",
      series: "",
      documentNumber: "",
      issueDate: new Date().toISOString().slice(0, 10),
      currencyCode: "USD",
      exchangeRate: "1",
      isNational: false,
      taxRate: "18",
      totalAmount: "",
      fobAmount: "",
      documentReference: "",
      notes: "",
    },
  ]);
  const [manualCosts, setManualCosts] = useState<ManualImportCost[]>([
    {
      costType: "freight",
      description: "",
      currencyCode: "USD",
      amount: "",
      exchangeRate: "1",
      capitalizable: true,
      recoverableTax: false,
      allocationMethod: "value",
    },
  ]);
  const [manualDuaType, setManualDuaType] = useState("DUA");
  const [manualDuaNumber, setManualDuaNumber] = useState("");
  const [manualDuaOffice, setManualDuaOffice] = useState("");
  const [manualDuaDate, setManualDuaDate] = useState("");
  const [manualDuaTc, setManualDuaTc] = useState("1");
  const [manualDuaFob, setManualDuaFob] = useState("");
  const [manualDuaFreight, setManualDuaFreight] = useState("");
  const [manualDuaInsurance, setManualDuaInsurance] = useState("");
  const [manualDuaAdjustments, setManualDuaAdjustments] = useState("");
  const [manualDuaCustomsValue, setManualDuaCustomsValue] = useState("");
  const [manualDuaAdValoremRate, setManualDuaAdValoremRate] = useState("0");
  const [manualDuaAdValoremManual, setManualDuaAdValoremManual] = useState("");
  const [manualDuaIscRate, setManualDuaIscRate] = useState("0");
  const [manualDuaIscManual, setManualDuaIscManual] = useState("");
  const [manualDuaIgvRate, setManualDuaIgvRate] = useState("18");
  const [manualDuaIgvManual, setManualDuaIgvManual] = useState("");
  const [manualDuaPerceptionRate, setManualDuaPerceptionRate] = useState("0");
  const [manualDuaPerceptionManual, setManualDuaPerceptionManual] = useState("");

  const load = useCallback(async () => {
    if (!organizationId) {
      setRows([]);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "list_organization_import_shipments",
      {
        p_organization_id: organizationId,
        p_search: search.trim() || null,
      },
    );

    if (rpcError) {
      setRows([]);
      setError(rpcError.message);
      setLoading(false);
      return;
    }

    setRows((data ?? []) as ImportRow[]);
    setLoading(false);
  }, [organizationId, search]);

  const loadEligibleOrders = useCallback(async () => {
    if (!organizationId) {
      setEligibleOrders([]);
      return;
    }

    setEligibleLoading(true);

    const { data, error: rpcError } = await supabase.rpc(
      "list_import_eligible_purchase_orders",
      {
        p_organization_id: organizationId,
      },
    );

    setEligibleLoading(false);

    if (rpcError) {
      setEligibleOrders([]);
      setCreateError(rpcError.message);
      return;
    }

    setEligibleOrders((data ?? []) as EligiblePurchaseOrder[]);
  }, [organizationId]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 220);
    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    void loadEligibleOrders();
  }, [loadEligibleOrders]);

  useEffect(() => {
    async function loadManualOptions() {
      if (!organizationId) {
        setSuppliers([]);
        setCatalog([]);
        return;
      }

      const [suppliersResult, catalogResult] = await Promise.all([
        supabase.rpc("list_organization_suppliers", {
          p_organization_id: organizationId,
          p_search: null,
        }),
        supabase.rpc("list_organization_catalog", {
          p_organization_id: organizationId,
          p_search: null,
          p_item_type: "product",
        }),
      ]);

      if (suppliersResult.error || catalogResult.error) {
        setCreateError(
          suppliersResult.error?.message ??
            catalogResult.error?.message ??
            "No se pudieron cargar los datos para la importación.",
        );
      }

      setSuppliers(
        suppliersResult.error
          ? []
          : ((suppliersResult.data ?? []) as SupplierOption[]).filter(
              (supplier) => supplier.status === "active",
            ),
      );
      setCatalog(
        catalogResult.error
          ? []
          : ((catalogResult.data ?? []) as CatalogOption[]).filter(
              (item) => item.status === "active",
            ),
      );
    }

    void loadManualOptions();
  }, [organizationId]);

  useEffect(() => {
    setPurchaseOrderId("");
    setOriginCountryCode("");
    setIncoterm("");
    setShipmentDate("");
    setEstimatedArrivalDate("");
    setTransportReference("");
    setAllocationMethod("value");
    setNotes("");
    setCreateError(null);
    setCreateMessage(null);
    setManualOpen(false);
    setManualSupplierId("");
    setManualCurrencyCode("USD");
    setManualOriginCountryCode("");
    setManualIncoterm("");
    setManualShipmentDate("");
    setManualEstimatedArrivalDate("");
    setManualTransportReference("");
    setManualAllocationMethod("value");
    setManualNotes("");
    setManualLines([
      {
        catalogItemId: "",
        description: "",
        quantity: "1",
        unitCost: "0",
        taxRate: "0",
      },
    ]);
    setManualDocuments([
      {
        documentType: "invoice",
        documentScope: "foreign",
        series: "",
        documentNumber: "",
        issueDate: new Date().toISOString().slice(0, 10),
        currencyCode: "USD",
        exchangeRate: "1",
        isNational: false,
        taxRate: "18",
        totalAmount: "",
        fobAmount: "",
        documentReference: "",
        notes: "",
      },
    ]);
    setManualCosts([
      {
        costType: "freight",
        description: "",
        currencyCode: "USD",
        amount: "",
        exchangeRate: "1",
        capitalizable: true,
        recoverableTax: false,
        allocationMethod: "value",
      },
    ]);
    setManualDuaType("DUA");
    setManualDuaNumber("");
    setManualDuaOffice("");
    setManualDuaDate("");
    setManualDuaTc("1");
    setManualDuaFob("");
    setManualDuaFreight("");
    setManualDuaInsurance("");
    setManualDuaAdjustments("");
    setManualDuaCustomsValue("");
    setManualDuaAdValoremRate("0");
    setManualDuaAdValoremManual("");
    setManualDuaIscRate("0");
    setManualDuaIscManual("");
    setManualDuaIgvRate("18");
    setManualDuaIgvManual("");
    setManualDuaPerceptionRate("0");
    setManualDuaPerceptionManual("");
  }, [organizationId]);

  const openCount = useMemo(
    () =>
      rows.filter(
        (row) => !["closed", "cancelled"].includes(row.status.toLowerCase()),
      ).length,
    [rows],
  );

  const landedCost = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.cost_total_pen || 0), 0),
    [rows],
  );

  function selectPurchaseOrder(nextId: string) {
    setPurchaseOrderId(nextId);
    const selected = eligibleOrders.find((order) => order.id === nextId);
    setIncoterm(selected?.incoterm ?? "");
  }

  function updateManualLine(
    index: number,
    patch: Partial<ManualImportLine>,
  ) {
    setManualLines((current) =>
      current.map((line, currentIndex) =>
        currentIndex === index ? { ...line, ...patch } : line,
      ),
    );
  }

  function chooseManualCatalog(index: number, catalogItemId: string) {
    const item = catalog.find((candidate) => candidate.id === catalogItemId);
    updateManualLine(index, {
      catalogItemId,
      description: item?.name ?? "",
      unitCost: String(item?.cost_price ?? 0),
      taxRate: String(item?.tax_rate ?? 0),
    });
  }

  function updateManualDocument(index: number, patch: Partial<ManualImportDocument>) {
    setManualDocuments((current) =>
      current.map((document, currentIndex) =>
        currentIndex === index ? { ...document, ...patch } : document,
      ),
    );
  }

  function updateManualCost(index: number, patch: Partial<ManualImportCost>) {
    setManualCosts((current) =>
      current.map((cost, currentIndex) =>
        currentIndex === index ? { ...cost, ...patch } : cost,
      ),
    );
  }

  function optionalNumber(value: string) {
    return value.trim() === "" ? null : Number(value);
  }

  async function createManualShipment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId || !manualSupplierId) {
      setCreateError("Selecciona un proveedor.");
      return;
    }

    const origin = manualOriginCountryCode.trim().toUpperCase();
    const normalizedIncoterm = manualIncoterm.trim().toUpperCase();
    const currency = manualCurrencyCode.trim().toUpperCase();

    if (!/^[A-Z]{3}$/.test(currency)) {
      setCreateError("La moneda debe tener 3 letras, por ejemplo USD o EUR.");
      return;
    }

    if (origin && !/^[A-Z]{2}$/.test(origin)) {
      setCreateError("El país de origen debe tener 2 letras, por ejemplo US o CN.");
      return;
    }

    if (normalizedIncoterm && !/^[A-Z]{3}$/.test(normalizedIncoterm)) {
      setCreateError("El Incoterm debe tener 3 letras, por ejemplo FOB o CIF.");
      return;
    }

    if (
      manualShipmentDate &&
      manualEstimatedArrivalDate &&
      manualEstimatedArrivalDate < manualShipmentDate
    ) {
      setCreateError("La llegada estimada no puede ser anterior a la fecha de embarque.");
      return;
    }

    const items = manualLines.map((line) => ({
      catalog_item_id: line.catalogItemId || null,
      description: line.description.trim(),
      quantity: Number(line.quantity),
      unit_cost: Number(line.unitCost),
      discount_amount: 0,
      tax_rate: Number(line.taxRate),
    }));

    if (
      items.some(
        (item) =>
          !item.catalog_item_id ||
          !item.description ||
          !Number.isFinite(item.quantity) ||
          item.quantity <= 0 ||
          !Number.isFinite(item.unit_cost) ||
          item.unit_cost < 0 ||
          !Number.isFinite(item.tax_rate) ||
          item.tax_rate < 0,
      )
    ) {
      setCreateError("Revisa SKU, descripción, cantidad, costo e impuesto de cada línea.");
      return;
    }

    const documentsToCreate = manualDocuments.filter(
      (document) => document.documentNumber.trim() !== "",
    );

    for (const document of documentsToCreate) {
      const total = Number(document.totalAmount || 0);
      const exchangeRate = Number(document.exchangeRate || 1);
      const taxRate = Number(document.taxRate || 0);
      const fob = Number(document.fobAmount || 0);
      if (
        !Number.isFinite(total) ||
        total < 0 ||
        !Number.isFinite(exchangeRate) ||
        exchangeRate <= 0 ||
        !Number.isFinite(taxRate) ||
        taxRate < 0 ||
        !Number.isFinite(fob) ||
        fob < 0
      ) {
        setCreateError("Revisa montos, tipo de cambio e impuestos de los documentos.");
        return;
      }
    }

    const costsToCreate = manualCosts.filter(
      (cost) => cost.description.trim() !== "" || cost.amount.trim() !== "",
    );

    for (const cost of costsToCreate) {
      const amount = Number(cost.amount);
      const exchangeRate = Number(cost.exchangeRate || 1);
      if (
        !cost.description.trim() ||
        !Number.isFinite(amount) ||
        amount < 0 ||
        !Number.isFinite(exchangeRate) ||
        exchangeRate <= 0
      ) {
        setCreateError("Revisa descripción, monto y tipo de cambio de los costos.");
        return;
      }
    }

    if (manualDuaNumber.trim()) {
      const numericDuaValues = [
        manualDuaTc,
        manualDuaFob || "0",
        manualDuaFreight || "0",
        manualDuaInsurance || "0",
        manualDuaAdjustments || "0",
        manualDuaAdValoremRate || "0",
        manualDuaIscRate || "0",
        manualDuaIgvRate || "0",
        manualDuaPerceptionRate || "0",
      ].map(Number);
      if (numericDuaValues.some((value) => !Number.isFinite(value) || value < 0) || Number(manualDuaTc) <= 0) {
        setCreateError("Revisa los valores de DUA/DAM, tributos y tipo de cambio.");
        return;
      }
    }

    setManualCreating(true);
    setCreateError(null);
    setCreateMessage(null);

    const { data: createdImportId, error: createImportError } = await supabase.rpc(
      "create_manual_import_shipment",
      {
        p_organization_id: organizationId,
        p_supplier_id: manualSupplierId,
        p_warehouse_id: null,
        p_currency_code: currency,
        p_origin_country_code: origin || null,
        p_incoterm: normalizedIncoterm || null,
        p_shipment_date: manualShipmentDate || null,
        p_estimated_arrival_date: manualEstimatedArrivalDate || null,
        p_transport_reference: manualTransportReference.trim() || null,
        p_notes: manualNotes.trim() || null,
        p_allocation_method: manualAllocationMethod,
        p_items: items,
      },
    );

    if (createImportError || !createdImportId) {
      setManualCreating(false);
      setCreateError(createImportError?.message ?? "No se pudo crear la importación.");
      return;
    }

    const importId = String(createdImportId);

    for (const document of documentsToCreate) {
      const { error: documentError } = await supabase.rpc("create_import_document", {
        p_import_shipment_id: importId,
        p_supplier_id: manualSupplierId,
        p_document_type: document.documentType,
        p_document_scope: document.documentScope,
        p_series: document.series.trim() || null,
        p_document_number: document.documentNumber.trim(),
        p_issue_date: document.issueDate || null,
        p_currency_code: document.currencyCode.trim().toUpperCase(),
        p_exchange_rate: Number(document.exchangeRate || 1),
        p_is_national: document.isNational,
        p_total_amount: Number(document.totalAmount || 0),
        p_tax_rate: Number(document.taxRate || 0),
        p_taxable_amount: null,
        p_tax_amount: null,
        p_fob_amount: Number(document.fobAmount || 0),
        p_document_reference: document.documentReference.trim() || null,
        p_notes: document.notes.trim() || null,
        p_items: [],
      });
      if (documentError) {
        setManualCreating(false);
        setCreateError(
          `La importación ${importId} fue creada, pero falló un documento: ${documentError.message}`,
        );
        navigate(`/importaciones/${importId}`);
        return;
      }
    }

    if (manualDuaNumber.trim()) {
      const { error: duaError } = await supabase.rpc("create_import_customs_calculation", {
        p_import_shipment_id: importId,
        p_declaration_type: manualDuaType,
        p_declaration_number: manualDuaNumber.trim(),
        p_customs_office: manualDuaOffice.trim() || null,
        p_declaration_date: manualDuaDate || null,
        p_exchange_rate: Number(manualDuaTc || 1),
        p_fob_amount: Number(manualDuaFob || 0),
        p_freight_amount: Number(manualDuaFreight || 0),
        p_insurance_amount: Number(manualDuaInsurance || 0),
        p_other_adjustments_amount: Number(manualDuaAdjustments || 0),
        p_customs_value_amount: optionalNumber(manualDuaCustomsValue),
        p_ad_valorem_rate: Number(manualDuaAdValoremRate || 0),
        p_ad_valorem_amount_pen: optionalNumber(manualDuaAdValoremManual),
        p_isc_rate: Number(manualDuaIscRate || 0),
        p_isc_amount_pen: optionalNumber(manualDuaIscManual),
        p_igv_rate: Number(manualDuaIgvRate || 0),
        p_igv_amount_pen: optionalNumber(manualDuaIgvManual),
        p_perception_rate: Number(manualDuaPerceptionRate || 0),
        p_perception_amount_pen: optionalNumber(manualDuaPerceptionManual),
        p_notes: null,
      });
      if (duaError) {
        setManualCreating(false);
        setCreateError(
          `La importación ${importId} fue creada, pero falló la DUA/DAM: ${duaError.message}`,
        );
        navigate(`/importaciones/${importId}`);
        return;
      }
    }

    for (const cost of costsToCreate) {
      const { error: costError } = await supabase.rpc("create_import_cost", {
        p_import_shipment_id: importId,
        p_supplier_id: null,
        p_cost_type: cost.costType,
        p_description: cost.description.trim(),
        p_currency_code: cost.currencyCode.trim().toUpperCase(),
        p_amount: Number(cost.amount || 0),
        p_exchange_rate: Number(cost.exchangeRate || 1),
        p_capitalizable: cost.capitalizable,
        p_recoverable_tax: cost.recoverableTax,
        p_document_reference: null,
        p_expense_date: new Date().toISOString().slice(0, 10),
        p_notes: null,
        p_allocation_method: cost.allocationMethod,
        p_purchase_order_item_id: null,
      });
      if (costError) {
        setManualCreating(false);
        setCreateError(
          `La importación ${importId} fue creada, pero falló un costo: ${costError.message}`,
        );
        navigate(`/importaciones/${importId}`);
        return;
      }
    }

    if (costsToCreate.length > 0) {
      const { error: landedError } = await supabase.rpc("calculate_import_landed_cost", {
        p_import_shipment_id: importId,
      });
      if (landedError) {
        setManualCreating(false);
        setCreateError(
          `La importación ${importId} fue creada, pero el costeo requiere revisión: ${landedError.message}`,
        );
        navigate(`/importaciones/${importId}`);
        return;
      }
    }

    setManualCreating(false);
    setCreateMessage("Importación creada con sus datos iniciales, documentos, aduana y costos.");
    await Promise.all([load(), loadEligibleOrders()]);
    navigate(`/importaciones/${importId}`);
  }

  async function createShipment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId || !purchaseOrderId) {
      setCreateError("Selecciona una orden de compra de importación.");
      return;
    }

    const origin = originCountryCode.trim().toUpperCase();
    const normalizedIncoterm = incoterm.trim().toUpperCase();

    if (origin && !/^[A-Z]{2}$/.test(origin)) {
      setCreateError("El país de origen debe tener 2 letras, por ejemplo US o CN.");
      return;
    }

    if (normalizedIncoterm && !/^[A-Z]{3}$/.test(normalizedIncoterm)) {
      setCreateError("El Incoterm debe tener 3 letras, por ejemplo FOB o CIF.");
      return;
    }

    if (
      shipmentDate &&
      estimatedArrivalDate &&
      estimatedArrivalDate < shipmentDate
    ) {
      setCreateError(
        "La llegada estimada no puede ser anterior a la fecha de embarque.",
      );
      return;
    }

    setCreating(true);
    setCreateError(null);
    setCreateMessage(null);

    const { data: createdImportId, error: rpcError } = await supabase.rpc(
      "create_import_shipment_from_purchase_order",
      {
        p_purchase_order_id: purchaseOrderId,
        p_origin_country_code: origin || null,
        p_incoterm: normalizedIncoterm || null,
        p_shipment_date: shipmentDate || null,
        p_estimated_arrival_date: estimatedArrivalDate || null,
        p_transport_reference: transportReference.trim() || null,
        p_notes: notes.trim() || null,
        p_allocation_method: allocationMethod,
      },
    );

    setCreating(false);

    if (rpcError) {
      setCreateError(rpcError.message);
      return;
    }

    setPurchaseOrderId("");
    setOriginCountryCode("");
    setIncoterm("");
    setShipmentDate("");
    setEstimatedArrivalDate("");
    setTransportReference("");
    setAllocationMethod("value");
    setNotes("");
    setCreateMessage("Embarque iniciado correctamente. Abriendo expediente, DUA y costeo…");

    await Promise.all([load(), loadEligibleOrders()]);
    if (createdImportId) {
      navigate(`/importaciones/${String(createdImportId)}`);
    }
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Importaciones</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Seguimiento de embarques, aduanas y costos asociados.</p>
        </div>
        <button
          className="primary-button"
          type="button"
          onClick={() => {
            setManualOpen((value) => !value);
            setCreateError(null);
            setCreateMessage(null);
          }}
        >
          {manualOpen ? "Cerrar" : "Nueva importación"}
        </button>
      </header>

      {manualOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Registro</span>
              <h2>Nueva importación</h2>
              <p>
                Registra desde el inicio proveedor, mercadería, logística,
                documentos, DUA/DAM, tributos y costos. Solo completa lo que
                ya tengas disponible; el expediente podrá seguir editándose.
              </p>
            </div>
          </div>

          <form className="imports-create-form" onSubmit={createManualShipment}>
            <div className="imports-form-section imports-field-span-2">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Datos generales</span>
                  <h3>Identificación y logística</h3>
                </div>
              </div>

              <div className="imports-section-grid">
                <label className="imports-field-span-2">
                  Proveedor
                  <select
                    required
                    value={manualSupplierId}
                    onChange={(event) => setManualSupplierId(event.target.value)}
                  >
                    <option value="">Seleccionar proveedor</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Moneda
                  <input
                    maxLength={3}
                    value={manualCurrencyCode}
                    onChange={(event) => setManualCurrencyCode(event.target.value.toUpperCase())}
                    placeholder="USD"
                  />
                </label>

                <label>
                  País de origen
                  <input
                    maxLength={2}
                    value={manualOriginCountryCode}
                    onChange={(event) => setManualOriginCountryCode(event.target.value.toUpperCase())}
                    placeholder="CN"
                  />
                </label>

                <label>
                  Incoterm
                  <input
                    maxLength={3}
                    value={manualIncoterm}
                    onChange={(event) => setManualIncoterm(event.target.value.toUpperCase())}
                    placeholder="FOB"
                  />
                </label>

                <label>
                  Fecha de embarque
                  <input
                    type="date"
                    value={manualShipmentDate}
                    onChange={(event) => setManualShipmentDate(event.target.value)}
                  />
                </label>

                <label>
                  Llegada estimada
                  <input
                    type="date"
                    value={manualEstimatedArrivalDate}
                    onChange={(event) => setManualEstimatedArrivalDate(event.target.value)}
                  />
                </label>

                <label>
                  Distribución de costos
                  <select
                    value={manualAllocationMethod}
                    onChange={(event) => setManualAllocationMethod(event.target.value)}
                  >
                    <option value="value">Por valor</option>
                    <option value="quantity">Por cantidad</option>
                    <option value="weight">Por peso</option>
                    <option value="volume">Por volumen</option>
                    <option value="direct">Directa</option>
                  </select>
                </label>

                <label className="imports-field-span-2">
                  Referencia de transporte
                  <input
                    value={manualTransportReference}
                    onChange={(event) => setManualTransportReference(event.target.value)}
                    placeholder="BL, AWB u otra referencia"
                  />
                </label>

                <label className="imports-field-span-2">
                  Notas
                  <textarea
                    rows={3}
                    value={manualNotes}
                    onChange={(event) => setManualNotes(event.target.value)}
                  />
                </label>
              </div>
            </div>

            <div className="imports-manual-lines imports-field-span-2">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Mercadería</span>
                  <h3>SKU de la importación</h3>
                </div>
                <button
                  className="compact-button"
                  type="button"
                  onClick={() =>
                    setManualLines((current) => [
                      ...current,
                      {
                        catalogItemId: "",
                        description: "",
                        quantity: "1",
                        unitCost: "0",
                        taxRate: "0",
                      },
                    ])
                  }
                >
                  Agregar línea
                </button>
              </div>

              {manualLines.map((line, index) => (
                <div className="imports-manual-line" key={index}>
                  <label>
                    SKU / producto
                    <select
                      required
                      value={line.catalogItemId}
                      onChange={(event) =>
                        chooseManualCatalog(index, event.target.value)
                      }
                    >
                      <option value="">Seleccionar</option>
                      {catalog.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.sku ?? "Sin SKU"} · {item.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Cantidad
                    <input
                      type="number"
                      min="0.000001"
                      step="0.000001"
                      value={line.quantity}
                      onChange={(event) =>
                        updateManualLine(index, { quantity: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    Costo unitario
                    <input
                      type="number"
                      min="0"
                      step="0.000001"
                      value={line.unitCost}
                      onChange={(event) =>
                        updateManualLine(index, { unitCost: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    Impuesto %
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.taxRate}
                      onChange={(event) =>
                        updateManualLine(index, { taxRate: event.target.value })
                      }
                    />
                  </label>

                  {manualLines.length > 1 ? (
                    <button
                      className="compact-button compact-button-danger"
                      type="button"
                      onClick={() =>
                        setManualLines((current) =>
                          current.filter((_, currentIndex) => currentIndex !== index),
                        )
                      }
                    >
                      Quitar
                    </button>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="imports-manual-lines imports-field-span-2">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Documentos</span>
                  <h3>Expediente inicial</h3>
                </div>
                <button
                  className="compact-button"
                  type="button"
                  onClick={() =>
                    setManualDocuments((current) => [
                      ...current,
                      {
                        documentType: "invoice",
                        documentScope: "foreign",
                        series: "",
                        documentNumber: "",
                        issueDate: new Date().toISOString().slice(0, 10),
                        currencyCode: manualCurrencyCode || "USD",
                        exchangeRate: "1",
                        isNational: false,
                        taxRate: "18",
                        totalAmount: "",
                        fobAmount: "",
                        documentReference: "",
                        notes: "",
                      },
                    ])
                  }
                >
                  Agregar documento
                </button>
              </div>

              {manualDocuments.map((document, index) => (
                <div className="imports-document-card" key={index}>
                  <div className="imports-section-grid">
                    <label>
                      Tipo
                      <select
                        value={document.documentType}
                        onChange={(event) => updateManualDocument(index, { documentType: event.target.value })}
                      >
                        <option value="invoice">Factura / Invoice</option>
                        <option value="quotation">Cotización</option>
                        <option value="sales_note">Nota de venta</option>
                        <option value="credit_note">Nota de crédito</option>
                        <option value="debit_note">Nota de débito</option>
                        <option value="freight">Flete</option>
                        <option value="insurance">Seguro</option>
                        <option value="customs_agent">Agente de aduanas</option>
                        <option value="local_transport">Transporte local</option>
                        <option value="packing_list">Packing list</option>
                        <option value="receipt">Comprobante</option>
                        <option value="other">Otro</option>
                      </select>
                    </label>

                    <label>
                      Alcance
                      <select
                        value={document.documentScope}
                        onChange={(event) => updateManualDocument(index, { documentScope: event.target.value })}
                      >
                        <option value="foreign">Exterior</option>
                        <option value="domestic">Nacional</option>
                        <option value="customs_duty">Derechos aduaneros</option>
                        <option value="logistics">Logística</option>
                        <option value="other">Otro</option>
                      </select>
                    </label>

                    <label>
                      Serie
                      <input
                        value={document.series}
                        onChange={(event) => updateManualDocument(index, { series: event.target.value })}
                      />
                    </label>

                    <label>
                      Número / correlativo
                      <input
                        value={document.documentNumber}
                        onChange={(event) => updateManualDocument(index, { documentNumber: event.target.value })}
                        placeholder="Completa para registrar este documento"
                      />
                    </label>

                    <label>
                      Fecha
                      <input
                        type="date"
                        value={document.issueDate}
                        onChange={(event) => updateManualDocument(index, { issueDate: event.target.value })}
                      />
                    </label>

                    <label>
                      Moneda
                      <input
                        maxLength={3}
                        value={document.currencyCode}
                        onChange={(event) => updateManualDocument(index, { currencyCode: event.target.value.toUpperCase() })}
                      />
                    </label>

                    <label>
                      Tipo de cambio
                      <input
                        type="number"
                        min="0.000001"
                        step="0.000001"
                        value={document.exchangeRate}
                        onChange={(event) => updateManualDocument(index, { exchangeRate: event.target.value })}
                      />
                    </label>

                    <label>
                      Total
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={document.totalAmount}
                        onChange={(event) => updateManualDocument(index, { totalAmount: event.target.value })}
                      />
                    </label>

                    <label>
                      FOB
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={document.fobAmount}
                        onChange={(event) => updateManualDocument(index, { fobAmount: event.target.value })}
                      />
                    </label>

                    <label>
                      IGV / impuesto %
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={document.taxRate}
                        onChange={(event) => updateManualDocument(index, { taxRate: event.target.value })}
                      />
                    </label>

                    <label className="imports-checkbox">
                      <input
                        type="checkbox"
                        checked={document.isNational}
                        onChange={(event) => updateManualDocument(index, { isNational: event.target.checked })}
                      />
                      Documento nacional (calcular base e IGV)
                    </label>

                    <label>
                      Referencia
                      <input
                        value={document.documentReference}
                        onChange={(event) => updateManualDocument(index, { documentReference: event.target.value })}
                      />
                    </label>

                    <label className="imports-field-span-2">
                      Notas
                      <textarea
                        rows={2}
                        value={document.notes}
                        onChange={(event) => updateManualDocument(index, { notes: event.target.value })}
                      />
                    </label>
                  </div>

                  {manualDocuments.length > 1 ? (
                    <button
                      className="compact-button compact-button-danger"
                      type="button"
                      onClick={() =>
                        setManualDocuments((current) =>
                          current.filter((_, currentIndex) => currentIndex !== index),
                        )
                      }
                    >
                      Quitar documento
                    </button>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="imports-form-section imports-field-span-2">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Aduanas</span>
                  <h3>DUA / DAM y tributos</h3>
                  <p>Puede quedar vacío si todavía no existe declaración.</p>
                </div>
              </div>

              <div className="imports-section-grid">
                <label>
                  Tipo
                  <select value={manualDuaType} onChange={(event) => setManualDuaType(event.target.value)}>
                    <option value="DUA">DUA</option>
                    <option value="DAM">DAM</option>
                  </select>
                </label>
                <label>
                  Número
                  <input value={manualDuaNumber} onChange={(event) => setManualDuaNumber(event.target.value)} />
                </label>
                <label>
                  Aduana
                  <input value={manualDuaOffice} onChange={(event) => setManualDuaOffice(event.target.value)} />
                </label>
                <label>
                  Fecha
                  <input type="date" value={manualDuaDate} onChange={(event) => setManualDuaDate(event.target.value)} />
                </label>
                <label>
                  TC
                  <input type="number" min="0.000001" step="0.000001" value={manualDuaTc} onChange={(event) => setManualDuaTc(event.target.value)} />
                </label>
                <label>
                  FOB
                  <input type="number" min="0" step="0.01" value={manualDuaFob} onChange={(event) => setManualDuaFob(event.target.value)} />
                </label>
                <label>
                  Flete
                  <input type="number" min="0" step="0.01" value={manualDuaFreight} onChange={(event) => setManualDuaFreight(event.target.value)} />
                </label>
                <label>
                  Seguro
                  <input type="number" min="0" step="0.01" value={manualDuaInsurance} onChange={(event) => setManualDuaInsurance(event.target.value)} />
                </label>
                <label>
                  Otros ajustes
                  <input type="number" min="0" step="0.01" value={manualDuaAdjustments} onChange={(event) => setManualDuaAdjustments(event.target.value)} />
                </label>
                <label>
                  CIF / valor aduana manual
                  <input type="number" min="0" step="0.01" value={manualDuaCustomsValue} onChange={(event) => setManualDuaCustomsValue(event.target.value)} placeholder="Vacío = calcular" />
                </label>
                <label>
                  Ad Valorem %
                  <input type="number" min="0" step="0.01" value={manualDuaAdValoremRate} onChange={(event) => setManualDuaAdValoremRate(event.target.value)} />
                </label>
                <label>
                  Ad Valorem S/ manual
                  <input type="number" min="0" step="0.01" value={manualDuaAdValoremManual} onChange={(event) => setManualDuaAdValoremManual(event.target.value)} />
                </label>
                <label>
                  ISC %
                  <input type="number" min="0" step="0.01" value={manualDuaIscRate} onChange={(event) => setManualDuaIscRate(event.target.value)} />
                </label>
                <label>
                  ISC S/ manual
                  <input type="number" min="0" step="0.01" value={manualDuaIscManual} onChange={(event) => setManualDuaIscManual(event.target.value)} />
                </label>
                <label>
                  IGV %
                  <input type="number" min="0" step="0.01" value={manualDuaIgvRate} onChange={(event) => setManualDuaIgvRate(event.target.value)} />
                </label>
                <label>
                  IGV S/ manual
                  <input type="number" min="0" step="0.01" value={manualDuaIgvManual} onChange={(event) => setManualDuaIgvManual(event.target.value)} />
                </label>
                <label>
                  Percepción %
                  <input type="number" min="0" step="0.01" value={manualDuaPerceptionRate} onChange={(event) => setManualDuaPerceptionRate(event.target.value)} />
                </label>
                <label>
                  Percepción S/ manual
                  <input type="number" min="0" step="0.01" value={manualDuaPerceptionManual} onChange={(event) => setManualDuaPerceptionManual(event.target.value)} />
                </label>
              </div>
            </div>

            <div className="imports-manual-lines imports-field-span-2">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Costeo</span>
                  <h3>Gastos y costos adicionales</h3>
                </div>
                <button
                  className="compact-button"
                  type="button"
                  onClick={() =>
                    setManualCosts((current) => [
                      ...current,
                      {
                        costType: "other",
                        description: "",
                        currencyCode: manualCurrencyCode || "USD",
                        amount: "",
                        exchangeRate: "1",
                        capitalizable: true,
                        recoverableTax: false,
                        allocationMethod: manualAllocationMethod,
                      },
                    ])
                  }
                >
                  Agregar costo
                </button>
              </div>

              {manualCosts.map((cost, index) => (
                <div className="imports-document-card" key={index}>
                  <div className="imports-section-grid">
                    <label>
                      Tipo de costo
                      <select value={cost.costType} onChange={(event) => updateManualCost(index, { costType: event.target.value })}>
                        <option value="freight">Flete</option>
                        <option value="insurance">Seguro</option>
                        <option value="customs">Aduanas</option>
                        <option value="customs_agent">Agente de aduanas</option>
                        <option value="transport">Transporte</option>
                        <option value="storage">Almacenaje</option>\n                        <option value="international_transport">Transporte internacional</option>\n                        <option value="port_charge">Gasto portuario</option>\n                        <option value="terminal_charge">Gasto terminal</option>\n                        <option value="inspection">Inspección</option>
                        <option value="handling">Manipuleo</option>
                        <option value="bank_charge">Gasto bancario</option>
                        <option value="other">Otro</option>
                      </select>
                    </label>
                    <label>
                      Descripción
                      <input value={cost.description} onChange={(event) => updateManualCost(index, { description: event.target.value })} />
                    </label>
                    <label>
                      Moneda
                      <input maxLength={3} value={cost.currencyCode} onChange={(event) => updateManualCost(index, { currencyCode: event.target.value.toUpperCase() })} />
                    </label>
                    <label>
                      Monto
                      <input type="number" min="0" step="0.01" value={cost.amount} onChange={(event) => updateManualCost(index, { amount: event.target.value })} />
                    </label>
                    <label>
                      TC
                      <input type="number" min="0.000001" step="0.000001" value={cost.exchangeRate} onChange={(event) => updateManualCost(index, { exchangeRate: event.target.value })} />
                    </label>
                    <label>
                      Distribución
                      <select value={cost.allocationMethod} onChange={(event) => updateManualCost(index, { allocationMethod: event.target.value })}>
                        <option value="value">Por valor</option>
                        <option value="quantity">Por cantidad</option>
                        <option value="weight">Por peso</option>
                        <option value="volume">Por volumen</option>
                        <option value="direct">Directa</option>
                      </select>
                    </label>
                    <label className="imports-checkbox">
                      <input type="checkbox" checked={cost.capitalizable} onChange={(event) => updateManualCost(index, { capitalizable: event.target.checked })} />
                      Capitalizable
                    </label>
                    <label className="imports-checkbox">
                      <input type="checkbox" checked={cost.recoverableTax} onChange={(event) => updateManualCost(index, { recoverableTax: event.target.checked })} />
                      Impuesto recuperable
                    </label>
                  </div>

                  {manualCosts.length > 1 ? (
                    <button
                      className="compact-button compact-button-danger"
                      type="button"
                      onClick={() =>
                        setManualCosts((current) =>
                          current.filter((_, currentIndex) => currentIndex !== index),
                        )
                      }
                    >
                      Quitar costo
                    </button>
                  ) : null}
                </div>
              ))}
            </div>

            {createError ? <div className="form-error imports-field-span-2">{createError}</div> : null}
            {createMessage ? <div className="form-success imports-field-span-2">{createMessage}</div> : null}

            <div className="form-actions imports-field-span-2">
              <button
                className="primary-button"
                type="submit"
                disabled={manualCreating || suppliers.length === 0 || catalog.length === 0}
              >
                {manualCreating ? "Creando…" : "Crear importación"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="imports-summary-grid">
        <div className="metric-card">
          <span>Embarques visibles</span>
          <strong>{loading ? "…" : rows.length}</strong>
        </div>
        <div className="metric-card">
          <span>En proceso</span>
          <strong>{loading ? "…" : openCount}</strong>
        </div>
        <div className="metric-card">
          <span>Costos registrados</span>
          <strong>{loading ? "…" : money(landedCost)}</strong>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Nuevo embarque</span>
            <h2>Iniciar desde una orden de importación</h2>
            <p>
              Solo aparecen órdenes confirmadas con cantidades pendientes de
              embarque.
            </p>
          </div>
        </div>

        {createError ? <div className="form-error">{createError}</div> : null}
        {createMessage ? (
          <div className="form-success">{createMessage}</div>
        ) : null}

        {eligibleLoading ? (
          <p className="settings-empty">Buscando órdenes elegibles…</p>
        ) : eligibleOrders.length === 0 ? (
          <p className="settings-empty">
            No hay órdenes de importación pendientes para iniciar un embarque.
          </p>
        ) : (
          <form className="imports-create-form" onSubmit={createShipment}>
            <label className="imports-field-span-2">
              Orden de compra
              <select
                required
                value={purchaseOrderId}
                onChange={(event) => selectPurchaseOrder(event.target.value)}
              >
                <option value="">Seleccionar OC</option>
                {eligibleOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.purchase_number} · {order.supplier_name} ·{" "}
                    {order.currency_code} · {Number(order.remaining_item_count)} ítems
                  </option>
                ))}
              </select>
            </label>

            <label>
              País de origen
              <input
                value={originCountryCode}
                maxLength={2}
                placeholder="US"
                onChange={(event) =>
                  setOriginCountryCode(event.target.value.toUpperCase())
                }
              />
            </label>

            <label>
              Incoterm
              <input
                value={incoterm}
                maxLength={3}
                placeholder="FOB"
                onChange={(event) => setIncoterm(event.target.value.toUpperCase())}
              />
            </label>

            <label>
              Fecha de embarque
              <input
                type="date"
                value={shipmentDate}
                onChange={(event) => setShipmentDate(event.target.value)}
              />
            </label>

            <label>
              Llegada estimada
              <input
                type="date"
                value={estimatedArrivalDate}
                onChange={(event) =>
                  setEstimatedArrivalDate(event.target.value)
                }
              />
            </label>

            <label className="imports-field-span-2">
              Referencia de transporte
              <input
                value={transportReference}
                placeholder="BL, AWB u otra referencia"
                onChange={(event) =>
                  setTransportReference(event.target.value)
                }
              />
            </label>

            <label>
              Distribución de costos
              <select
                value={allocationMethod}
                onChange={(event) => setAllocationMethod(event.target.value)}
              >
                <option value="value">Por valor</option>
                <option value="quantity">Por cantidad</option>
                <option value="weight">Por peso</option>
                <option value="volume">Por volumen</option>
                <option value="direct">Directa</option>
              </select>
            </label>

            <label className="imports-field-span-2">
              Notas
              <textarea
                rows={3}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>

            <div className="form-actions imports-field-span-2">
              <button
                className="primary-button"
                type="submit"
                disabled={creating}
              >
                {creating ? "Iniciando…" : "Iniciar embarque"}
              </button>
            </div>
          </form>
        )}
      </section>

      <section className="toolbar-card">
        <div>
          <strong>{loading ? "…" : rows.length}</strong>
          <span> embarques</span>
        </div>
        <input
          type="search"
          placeholder="Buscar importación, orden o declaración"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {error ? <div className="inline-alert">{error}</div> : null}

      {!loading && rows.length === 0 && !error ? (
        <section className="empty-state">
          <strong>No hay importaciones para mostrar.</strong>
          <p>
            Inicia un embarque desde una orden de compra con alcance de
            importación.
          </p>
        </section>
      ) : null}

      <section className="imports-grid">
        {rows.map((row) => (
          <article className="imports-card" key={row.id}>
            <div className="imports-card-heading">
              <div>
                <span className="status-badge">{IMPORT_STATUS_LABELS[row.status.toLowerCase()] ?? row.status}</span>
                <h2>{row.import_number}</h2>
                <p>{row.purchase_number ?? "Sin orden vinculada"}</p>
              </div>
              <strong>{money(row.cost_total_pen)}</strong>
            </div>

            <dl className="customer-meta">
              <div>
                <dt>Origen</dt>
                <dd>{row.origin_country_code ?? "—"}</dd>
              </div>
              <div>
                <dt>Destino</dt>
                <dd>{row.destination_country_code ?? "—"}</dd>
              </div>
              <div>
                <dt>Incoterm</dt>
                <dd>{row.incoterm ?? "—"}</dd>
              </div>
              <div>
                <dt>Moneda</dt>
                <dd>{row.supplier_currency_code ?? "—"}</dd>
              </div>
              <div>
                <dt>Llegada estimada</dt>
                <dd>{row.estimated_arrival_date ?? "—"}</dd>
              </div>
              <div>
                <dt>Declaraciones</dt>
                <dd>{Number(row.declaration_count)}</dd>
              </div>
            </dl>

            <div className="quotation-actions">
              <Link
                className="compact-button"
                to={`/importaciones/${row.id}`}
              >
                Expediente, DUA y costeo
              </Link>
            </div>

            {row.customs_declaration_number ? (
              <p className="imports-customs">
                Aduanas: {row.customs_declaration_number}
              </p>
            ) : null}
          </article>
        ))}
      </section>
    </div>
  );
}
